import { useState } from "react";
import { Plus } from "lucide-react";
import { SkillEditorMode, type SkillFormData } from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsGroupState } from "~/pages/manifold-nextgen/components/settings/SettingsGroupState";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { type PartialSkill, type Skill } from "~/services/skills";
import { useUserSkills } from "./useUserSkills";

type SkillDialogState = { kind: "none" } | { kind: "editor"; skill?: Skill };

/**
 * The profile's Skills tab: the user's personal USER-scope skill library, which
 * every conversation they start can draw on.
 */
export const ProfileSkillsTab = () => {
  const user = useUser();
  const { showSuccess, showError } = useToast();

  const { skills, isLoading: skillsLoading, error: skillsError } = useUserSkills(user?.email);
  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  const [skillDialog, setSkillDialog] = useState<SkillDialogState>({ kind: "none" });
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);
  const [isSavingSkill, setIsSavingSkill] = useState(false);

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setSkillDialog({ kind: "editor", skill });
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleSkillSubmit = async (data: SkillFormData) => {
    setIsSavingSkill(true);
    try {
      if (skillDialog.kind === "editor" && skillDialog.skill) {
        await updateSkill(skillDialog.skill.id, {
          description: data.description,
          content: data.content,
        });
        showSuccess(`"${skillDialog.skill.name}" updated`);
      } else {
        await createUserSkill(data);
        showSuccess(`"${data.name}" added`);
      }
      setSkillDialog({ kind: "none" });
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setIsSavingSkill(false);
    }
  };

  const handleDeleteSkillConfirm = async (skill: PartialSkill) => {
    try {
      await deleteSkill(skill);
      showSuccess(`"${skill.name}" removed`);
    } catch (e) {
      showError("Unable to remove skill");
      throw e;
    }
  };

  const skillEditorInitialData =
    skillDialog.kind === "editor" && skillDialog.skill
      ? {
          name: skillDialog.skill.name,
          description: skillDialog.skill.description ?? "",
          content: skillDialog.skill.content,
        }
      : undefined;

  return (
    <SettingsPage
      title="Skills"
      description="Persistent, reusable instructions. Any agent you use can access them."
    >
      <SettingsGroup
        label="Your skills"
        count={skills.length}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSkillDialog({ kind: "editor" })}
            className="text-primary hover:text-primary/80"
          >
            <Plus />
            Add skill
          </Button>
        }
      >
        <SettingsGroupState
          isLoading={skillsLoading}
          hasError={Boolean(skillsError)}
          isEmpty={skills.length === 0}
          errorMessage="Unable to load your skills."
          emptyMessage="No skills yet."
          emptyHint="Add one for the guidance you find yourself repeating to an agent."
          skeletonRows={2}
        >
          <ul className="divide-y divide-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit
                onEdit={() => handleEditSkill(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        </SettingsGroupState>
      </SettingsGroup>

      <SkillEditorModal
        open={skillDialog.kind === "editor"}
        onClose={() => setSkillDialog({ kind: "none" })}
        onSubmit={handleSkillSubmit}
        loading={isSavingSkill}
        mode={
          skillDialog.kind === "editor" && skillDialog.skill
            ? SkillEditorMode.EDIT
            : SkillEditorMode.CREATE
        }
        initialData={skillEditorInitialData}
      />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Remove this skill?"
        description={(skill) => <>&ldquo;{skill.name}&rdquo; will be permanently deleted.</>}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleDeleteSkillConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </SettingsPage>
  );
};
